import { useState, type FormEvent } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const WHATSAPP_NUMBER = '919686543847';

function buildWhatsAppUrl(form: HTMLFormElement): string {
  const formData = new FormData(form);
  const name = ((formData.get('name') as string) || '').trim();
  const email = ((formData.get('email') as string) || '').trim();
  const phone = ((formData.get('phone') as string) || '').trim();
  const department = ((formData.get('department') as string) || '').trim();
  const subject = ((formData.get('subject') as string) || '').trim();
  const message = ((formData.get('message') as string) || '').trim();

  const text = [
    '*New Contact Form Submission*',
    '',
    `*Name:* ${name}`,
    `*Email:* ${email}`,
    `*Phone:* ${phone}`,
    `*Department:* ${department}`,
    `*Subject:* ${subject}`,
    '',
    '*Message:*',
    message,
  ].join('%0A');

  return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
}

export default function ContactFormSection() {
  const { ref, revealed } = useScrollReveal();
  const [isOpening, setIsOpening] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>(
      'input[name="company_alt"]'
    );
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      return;
    }

    setIsOpening(true);

    const whatsappUrl = buildWhatsAppUrl(form);
    window.open(whatsappUrl, '_blank');

    setTimeout(() => {
      form.reset();
      setCharCount(0);
      setIsOpening(false);
    }, 800);
  };

  return (
    <section id="contact-form" className="relative py-16 md:py-20 bg-brand-800 overflow-hidden">
      {/* Frost texture */}
      <div className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      ></div>

      <div
        ref={ref}
        className={`container-page relative z-10 scroll-reveal ${revealed ? 'revealed' : ''}`}
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-3 py-1 text-xs font-semibold uppercase tracking-widest text-secondary-300 bg-secondary-500/15 rounded-full mb-4">
              Send a Message
            </span>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-paper-50 mb-4">
              Drop Us a Line
            </h2>
            <p className="text-brand-200/70 max-w-lg mx-auto text-sm md:text-base">
              Fill out the form below and we'll get back to you — usually
              within 24 hours on business days.
            </p>
          </div>

          <div className="bg-brand-700/80 backdrop-blur-sm rounded-xl border border-brand-500/30 p-6 md:p-10 frost-glow">
            <form
              onSubmit={handleSubmit}
              className="space-y-6"
              noValidate
            >
              {/* Row 1: Full Name + Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-brand-200 mb-1.5"
                  >
                    Full Name <span className="text-accent-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="Your full name"
                    className="w-full px-4 py-3 text-sm bg-brand-800/60 border border-brand-500/30 rounded-lg text-paper-50 placeholder:text-brand-300/50 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-colors"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-brand-200 mb-1.5"
                  >
                    Email Address <span className="text-accent-400">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 text-sm bg-brand-800/60 border border-brand-500/30 rounded-lg text-paper-50 placeholder:text-brand-300/50 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-colors"
                  />
                </div>
              </div>

              {/* Row 2: Phone + Department */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-brand-200 mb-1.5"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="Your contact number"
                    className="w-full px-4 py-3 text-sm bg-brand-800/60 border border-brand-500/30 rounded-lg text-paper-50 placeholder:text-brand-300/50 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-colors"
                  />
                </div>
                <div>
                  <label
                    htmlFor="department"
                    className="block text-sm font-medium text-brand-200 mb-1.5"
                  >
                    Department <span className="text-accent-400">*</span>
                  </label>
                  <select
                    id="department"
                    name="department"
                    required
                    defaultValue=""
                    className="w-full px-4 py-3 text-sm bg-brand-800/60 border border-brand-500/30 rounded-lg text-paper-50 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-colors cursor-pointer"
                  >
                    <option value="" disabled>
                      Select a department
                    </option>
                    <option value="storage">Storage Partnerships</option>
                    <option value="transport">Transport Partnerships</option>
                    <option value="wholesale">Wholesale Trade Desk</option>
                    <option value="retail">Retail &amp; Consumer Support</option>
                    <option value="careers">Careers / Talent</option>
                    <option value="press">Press &amp; Media</option>
                    <option value="other">General Inquiry</option>
                  </select>
                </div>
              </div>

              {/* Row 3: Subject */}
              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-brand-200 mb-1.5"
                >
                  Subject <span className="text-accent-400">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  placeholder="What is this about?"
                  className="w-full px-4 py-3 text-sm bg-brand-800/60 border border-brand-500/30 rounded-lg text-paper-50 placeholder:text-brand-300/50 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-colors"
                />
              </div>

              {/* Row 4: Message */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-brand-200 mb-1.5"
                >
                  Message <span className="text-accent-400">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Tell us about your needs, questions, or how we can help..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 text-sm bg-brand-800/60 border border-brand-500/30 rounded-lg text-paper-50 placeholder:text-brand-300/50 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-colors resize-none"
                ></textarea>
                <div className="flex justify-end mt-1">
                  <span
                    className={`text-xs ${charCount > 480 ? 'text-accent-400' : 'text-brand-300/60'}`}
                  >
                    {charCount}/500
                  </span>
                </div>
              </div>

              {/* Honeypot - anti-spam */}
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="contact-honeypot"
              />

              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isOpening}
                  className={`w-full sm:w-auto px-8 py-3.5 rounded-lg font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                    isOpening
                      ? 'bg-brand-400 text-paper-50 cursor-not-allowed'
                      : 'bg-accent-500 hover:bg-accent-400 text-brand-800'
                  }`}
                >
                  {isOpening ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-whatsapp-line text-lg"></i>
                      Opening WhatsApp...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-whatsapp-line text-lg"></i>
                      Send via WhatsApp
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}